(() => {
  const SECTION_SELECTOR = '#dotd-universe-music';

  class DotdUniverseMusic {
    constructor(section) {
      this.section = section;
      this.activeModal = null;
      this.lastTrigger = null;
      this.openedFromShareHash = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.section.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);

      this.initAudio();
      this.openModalFromShareHash();
    }

    initAudio() {
      this.section
        .querySelectorAll('[data-music-audio]')
        .forEach((audio) => {
          audio.addEventListener('play', () => {
            this.updateAudioControls(audio, true);
          });

          audio.addEventListener('pause', () => {
            this.updateAudioControls(audio, false);
          });

          audio.addEventListener('ended', () => {
            this.updateAudioControls(audio, false);
          });
        });
    }

    openModalFromShareHash() {
      if (!window.location.hash) return;

      const shareKey = decodeURIComponent(
        window.location.hash.replace(/^#/, '')
      );

      if (!shareKey) return;

      const modal = this.section.querySelector(
        `[data-music-share-key="${CSS.escape(shareKey)}"]`
      );

      if (!modal || !modal.id) return;

      this.openedFromShareHash = true;

      this.openModal(
        modal.id,
        null,
        null
      );
    }

    handleClick(event) {
      const audioTrigger = event.target.closest('[data-music-audio-toggle]');

      if (audioTrigger && this.section.contains(audioTrigger)) {
        event.preventDefault();
        this.toggleAudio(audioTrigger);
        return;
      }

      const openTrigger = event.target.closest('[data-music-modal-open]');

      if (openTrigger && this.section.contains(openTrigger)) {
        event.preventDefault();

        const modalId = openTrigger.getAttribute('data-music-modal-open');
        const target = openTrigger.getAttribute('data-music-scroll-target');

        this.openedFromShareHash = false;

        this.openModal(
          modalId,
          openTrigger,
          target
        );

        return;
      }

      const modalNavTrigger = event.target.closest('[data-music-modal-nav]');

      if (modalNavTrigger && this.activeModal) {
        event.preventDefault();

        const target = modalNavTrigger.getAttribute('data-music-modal-nav');

        const section = this.activeModal.querySelector(
          `[data-music-detail="${CSS.escape(target)}"]`
        );

        if (section) {
          section.scrollIntoView({
            behavior: window.matchMedia(
              '(prefers-reduced-motion: reduce)'
            ).matches
              ? 'auto'
              : 'smooth',
            block: 'start'
          });
        }

        return;
      }

      const closeTrigger = event.target.closest('[data-music-modal-close]');

      if (closeTrigger && this.activeModal) {
        event.preventDefault();
        this.closeModal();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.activeModal) {
        this.closeModal();
      }

      if (event.key === 'Tab' && this.activeModal) {
        this.trapFocus(event);
      }
    }

    toggleAudio(trigger) {
      const audioId = trigger.getAttribute('aria-controls');

      if (!audioId) return;

      const audio = this.section.querySelector(
        `#${CSS.escape(audioId)}`
      );

      if (!audio) return;

      this.section
        .querySelectorAll('[data-music-audio]')
        .forEach((otherAudio) => {
          if (
            otherAudio !== audio &&
            !otherAudio.paused
          ) {
            otherAudio.pause();
          }
        });

      if (audio.paused) {
        const playPromise = audio.play();

        if (
          playPromise &&
          typeof playPromise.catch === 'function'
        ) {
          playPromise.catch(() => {
            this.updateAudioControls(audio, false);
          });
        }
      } else {
        audio.pause();
      }
    }

    updateAudioControls(audio, isPlaying) {
      if (!audio.id) return;

      this.section
        .querySelectorAll('[data-music-audio-toggle]')
        .forEach((trigger) => {
          if (
            trigger.getAttribute('aria-controls') !== audio.id
          ) {
            return;
          }

          trigger.setAttribute(
            'aria-pressed',
            isPlaying ? 'true' : 'false'
          );

          const label = trigger.querySelector(
            '[data-music-audio-label]'
          );

          const icon = trigger.querySelector(
            '[data-music-audio-icon]'
          );

          if (label) {
            label.textContent = isPlaying
              ? 'PAUSE / 一時停止'
              : 'LISTEN / 再生する';
          }

          if (icon) {
            icon.textContent = isPlaying
              ? '❚❚'
              : '▶';
          }
        });
    }

    openModal(modalId, trigger, scrollTarget) {
      const modal = this.section.querySelector(
        `#${CSS.escape(modalId)}`
      );

      if (!modal) return;

      this.lastTrigger = trigger;
      this.activeModal = modal;

      document.documentElement.classList.add(
        'dotd-music-modal-open'
      );

      document.body.classList.add(
        'dotd-music-modal-open'
      );

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        modal.classList.add('is-visible');

        const closeButton = modal.querySelector(
          '.dotd-music-modal__close'
        );

        if (closeButton) {
          closeButton.focus();
        }

        if (scrollTarget) {
          window.setTimeout(() => {
            const section = modal.querySelector(
              `[data-music-detail="${CSS.escape(scrollTarget)}"]`
            );

            if (section) {
              section.scrollIntoView({
                behavior: window.matchMedia(
                  '(prefers-reduced-motion: reduce)'
                ).matches
                  ? 'auto'
                  : 'smooth',
                block: 'start'
              });
            }
          }, 240);
        } else {
          const panel = modal.querySelector(
            '.dotd-music-modal__panel'
          );

          if (panel) {
            panel.scrollTop = 0;
          }
        }
      });
    }

    closeModal() {
      if (!this.activeModal) return;

      const modal = this.activeModal;

      modal.classList.remove('is-visible');

      window.setTimeout(() => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');

        document.documentElement.classList.remove(
          'dotd-music-modal-open'
        );

        document.body.classList.remove(
          'dotd-music-modal-open'
        );

        this.activeModal = null;

        if (this.lastTrigger) {
          this.lastTrigger.focus();
          this.lastTrigger = null;
        }

        if (this.openedFromShareHash) {
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}${window.location.search}`
          );

          this.openedFromShareHash = false;
        }
      }, 230);
    }

    trapFocus(event) {
      const focusable = this.activeModal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function init() {
    document
      .querySelectorAll(SECTION_SELECTOR)
      .forEach((section) => {
        if (
          section.dataset.musicInitialized === 'true'
        ) {
          return;
        }

        section.dataset.musicInitialized = 'true';

        new DotdUniverseMusic(section);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    init
  );
})();